import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  leadId: string;
  submission: { id: string; full_name: string } | null;
  onMoved?: () => void;
}

export function MoveSubmissionDialog({ open, onOpenChange, leadId, submission, onMoved }: Props) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [targetId, setTargetId] = useState<string | null>(null);

  const leadsQ = useQuery({
    queryKey: ["move-intake-leads", search],
    enabled: open,
    queryFn: async () => {
      let q = (supabase as any)
        .from("leads")
        .select("id, title, client_name, brand")
        .neq("id", leadId)
        .order("created_at", { ascending: false })
        .limit(30);
      if (search.trim()) q = q.ilike("title", `%${search.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  const moveMut = useMutation({
    mutationFn: async () => {
      if (!submission || !targetId) throw new Error("Pick a programme first");
      const { error } = await (supabase as any).rpc("move_intake_submission", {
        _submission_id: submission.id,
        _target_lead_id: targetId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Entry moved to the selected programme");
      qc.invalidateQueries({ queryKey: ["student-intake-subs"] });
      qc.invalidateQueries({ queryKey: ["documents"] });
      setTargetId(null);
      setSearch("");
      onOpenChange(false);
      onMoved?.();
    },
    onError: (e: any) => toast.error(e?.message || "Move failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!moveMut.isPending) onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" /> Move to another programme
          </DialogTitle>
          <DialogDescription>
            {submission
              ? `${submission.full_name}'s entry and their uploaded photos will move across together.`
              : "Select an entry to move."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="move-search">Find the correct programme</Label>
            <Input
              id="move-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by programme name"
            />
          </div>
          <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
            {leadsQ.isLoading ? (
              <div className="p-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
            ) : !leadsQ.data?.length ? (
              <p className="p-4 text-sm text-muted-foreground text-center">No programmes found.</p>
            ) : (
              (leadsQ.data as any[]).map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setTargetId(l.id)}
                  className={`w-full text-left p-3 text-sm hover:bg-muted transition-colors ${targetId === l.id ? "bg-muted" : ""}`}
                >
                  <span className="font-medium">{l.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {[l.client_name, l.brand].filter(Boolean).join(" · ") || "—"}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={moveMut.isPending}>Cancel</Button>
          <Button onClick={() => moveMut.mutate()} disabled={!targetId || !submission || moveMut.isPending}>
            {moveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Move entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
